import {Component, EventEmitter, Output} from '@angular/core';
import {OuvrageService} from "../../services/ouvrage.service";
import {parseBulkImportCsv} from "./csv-eans-parser";
import {BulkReviewRow} from "./bulk-ouvrage.model";

type BulkStep = 'import' | 'review' | 'result';

@Component({
  selector: 'app-bulk-create-ouvrage',
  templateUrl: './bulk-create-ouvrage.component.html',
  styleUrls: ['./bulk-create-ouvrage.component.scss']
})
export class BulkCreateOuvrageComponent {

  @Output() ouvragesCreated = new EventEmitter<void>();

  displayDialog: boolean = false;
  showFormatHelp: boolean = false;
  step: BulkStep = 'import';
  loading: boolean = false;
  globalError: string | null = null;

  reviewRows: BulkReviewRow[] = [];
  finalSuccessCount: number = 0;
  finalTotalCount: number = 0;

  constructor(private ouvrageService: OuvrageService) {
  }

  open(): void {
    this.displayDialog = true;
    this.resetState();
  }

  close(): void {
    this.displayDialog = false;
    this.resetState();
  }

  toggleFormatHelp(): void {
    this.showFormatHelp = !this.showFormatHelp;
  }

  /**
   * Retourne une Promise résolue une fois le fichier lu (et, le cas échéant, la requête
   * search_by_eans lancée) : ignoré par le binding (change) du template, mais permet aux
   * tests d'attendre la fin de la lecture asynchrone du FileReader sans "tick()" fictif.
   */
  onFileSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files.length > 0 ? input.files[0] : null;
    input.value = '';

    if (!file) {
      return Promise.resolve();
    }

    this.globalError = null;
    this.loading = true;

    return new Promise<void>((resolve) => {
      const reader = new FileReader();
      reader.onload = () => {
        const content = typeof reader.result === 'string' ? reader.result : '';
        const {lines, errors} = parseBulkImportCsv(content);

        const parseErrorRows: BulkReviewRow[] = errors.map(error => ({
          ean: error.raw,
          codeProjet: '',
          valid: false,
          errorMessage: error.message,
          baseArticleId: null,
          titreWeb: null,
          ouvrageReforme: false,
        }));

        if (lines.length === 0) {
          this.reviewRows = parseErrorRows;
          this.step = 'review';
          this.loading = false;
          resolve();
          return;
        }

        this.ouvrageService.searchBaseArticlesByEans(lines).subscribe({
          next: (response) => {
            const resolvedRows: BulkReviewRow[] = response.results.map(result => ({
              ...result,
              ouvrageReforme: false,
            }));
            this.reviewRows = [...parseErrorRows, ...resolvedRows];
            this.step = 'review';
            this.loading = false;
          },
          error: () => {
            this.globalError = 'Impossible de vérifier les EAN importés auprès du PIM. Réessayez';
            this.loading = false;
          },
        });
        resolve();
      };
      reader.onerror = () => {
        this.globalError = 'Impossible de lire le fichier importé';
        this.loading = false;
        resolve();
      };
      reader.readAsText(file, 'utf-8');
    });
  }

  get validRowsCount(): number {
    return this.reviewRows.filter(row => row.valid).length;
  }

  get failedRows(): BulkReviewRow[] {
    return this.reviewRows.filter(row => !row.valid);
  }

  onValidate(): void {
    const validRows = this.reviewRows.filter(row => row.valid);
    if (validRows.length === 0) {
      return;
    }

    this.globalError = null;
    this.loading = true;

    const items = validRows.map(row => ({
      ean: row.ean,
      codeProjet: row.codeProjet,
      ouvrageReforme: row.ouvrageReforme,
    }));

    this.ouvrageService.createOuvragesBulk(items).subscribe({
      next: (report) => {
        report.results.forEach((result, index) => {
          const row = validRows[index];
          row.valid = result.valid;
          row.errorMessage = result.errorMessage;
          row.id = result.id;
        });

        this.finalSuccessCount = report.successCount;
        this.finalTotalCount = this.reviewRows.length;
        this.step = 'result';
        this.loading = false;
        this.ouvragesCreated.emit();
      },
      error: () => {
        this.globalError = 'Impossible de créer les projets. Réessayez';
        this.loading = false;
      },
    });
  }

  reimport(): void {
    this.resetState();
  }

  private resetState(): void {
    this.step = 'import';
    this.showFormatHelp = false;
    this.loading = false;
    this.globalError = null;
    this.reviewRows = [];
    this.finalSuccessCount = 0;
    this.finalTotalCount = 0;
  }
}
